import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../components/risikoanalyseStep/tokens";
import { blobAlsDataUrl, canvasAlsPng, inhaltsRahmen } from "./bildVorbereiten";

/**
 * Zeichenfläche für die Unterschrift: Maus, Stift und Finger (Pointer Events,
 * `touch-action: none`, damit der Finger nicht die Seite scrollt). Zwischen den Punkten
 * wird quadratisch geglättet (Mittelpunkt-Verfahren), sonst wirkt die Linie eckig.
 *
 * Nach jedem abgeschlossenen Strich meldet `onAenderung` das Ergebnis als PNG-Data-URL —
 * auf die Striche zugeschnitten (+ 8 px), durchsichtiger Hintergrund. Leer → null.
 */

const BREITE = 600;
const HOEHE = 200;
const RAND = 8;

/**
 * Wo eine vorhandene (zugeschnittene) Zeichnung beim erneuten Öffnen auf der Fläche landet:
 * mittig, in CSS-Pixeln (`px / dpr`), bei Bedarf verkleinert, damit sie ganz hineinpasst.
 */
export function vorhandenPlatzieren(
    pxBreite: number,
    pxHoehe: number,
    dpr: number,
): { x: number; y: number; w: number; h: number } {
    let w = pxBreite / dpr;
    let h = pxHoehe / dpr;
    const f = Math.min(1, BREITE / w, HOEHE / h);
    w *= f;
    h *= f;
    return { x: (BREITE - w) / 2, y: (HOEHE - h) / 2, w, h };
}

type Punkt = { x: number; y: number };

/**
 * Was beim Absetzen noch fehlt: Die Glättung malt Kurven nur von Mitte zu Mitte — das
 * letzte halbe Teilstück (und ein Strich aus nur zwei Punkten) bliebe sonst ungemalt.
 */
export function schlussStueck(pts: Punkt[]): [Punkt, Punkt] | null {
    if (pts.length < 2) return null;
    if (pts.length === 2) return [pts[0], pts[1]];
    const [b, c] = pts.slice(-2);
    return [{ x: (b.x + c.x) / 2, y: (b.y + c.y) / 2 }, c];
}

const useStyles = makeStyles({
    wurzel: { display: "flex", flexDirection: "column", rowGap: "6px", maxWidth: `${BREITE}px` },
    flaeche: {
        width: "100%",
        maxWidth: `${BREITE}px`,
        aspectRatio: `${BREITE} / ${HOEHE}`,
        touchAction: "none",
        cursor: "crosshair",
        backgroundColor: REFINED.white,
        ...shorthands.border("1px", "dashed", REFINED.aub200),
        ...shorthands.borderRadius("10px"),
    },
    fuss: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.75rem", color: REFINED.ink3 },
    link: {
        appearance: "none",
        ...shorthands.border("0"),
        backgroundColor: "transparent",
        color: REFINED.aub,
        fontWeight: 600,
        cursor: "pointer",
        fontSize: "0.8rem",
        ...shorthands.padding("2px", "4px"),
    },
});

export const UnterschriftZeichnen: React.FC<{
    farbe: string;
    onAenderung: (dataUrl: string | null) => void;
    /**
     * Bereits gezeichnete Unterschrift (PNG-Data-URL). Nach „Tippen“ → „Zeichnen“ steht sie
     * wieder auf der Fläche und lässt sich ergänzen oder leeren — statt still weiterzugelten.
     */
    vorhanden?: string | null;
}> = ({ farbe, onAenderung, vorhanden }) => {
    const s = useStyles();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const punkte = React.useRef<Punkt[]>([]);
    const zeichnet = React.useRef(false);
    const [leer, setLeer] = React.useState(!vorhanden);
    // Nur beim Aufbau übernehmen — danach gehört die Fläche dem Nutzer.
    const start = React.useRef(vorhanden ?? null);

    // Canvas-Pixel = CSS-Maß × Gerätepixel, damit der Strich auf hochauflösenden Displays scharf ist.
    React.useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
        c.width = BREITE * dpr;
        c.height = HOEHE * dpr;
        const ctx = c.getContext("2d");
        if (!ctx) return;
        ctx.scale(dpr, dpr);
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = 2.5;
        const url = start.current;
        if (!url) return;
        const img = new Image();
        img.onload = () => {
            const r = vorhandenPlatzieren(img.naturalWidth, img.naturalHeight, dpr);
            ctx.drawImage(img, r.x, r.y, r.w, r.h);
        };
        img.src = url;
    }, []);

    const punktVon = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const r = e.currentTarget.getBoundingClientRect();
        return { x: ((e.clientX - r.left) / r.width) * BREITE, y: ((e.clientY - r.top) / r.height) * HOEHE };
    };

    const exportieren = React.useCallback(async () => {
        const c = canvasRef.current;
        const ctx = c?.getContext("2d");
        if (!c || !ctx) return;
        const daten = ctx.getImageData(0, 0, c.width, c.height);
        const dpr = c.width / BREITE;
        const rahmen = inhaltsRahmen(daten.data, c.width, c.height, Math.round(RAND * dpr));
        if (!rahmen) {
            onAenderung(null);
            return;
        }
        const aus = document.createElement("canvas");
        aus.width = rahmen.w;
        aus.height = rahmen.h;
        aus.getContext("2d")?.drawImage(c, rahmen.x, rahmen.y, rahmen.w, rahmen.h, 0, 0, rahmen.w, rahmen.h);
        onAenderung(await blobAlsDataUrl(await canvasAlsPng(aus)));
    }, [onAenderung]);

    const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        e.currentTarget.setPointerCapture?.(e.pointerId);
        zeichnet.current = true;
        const p = punktVon(e);
        punkte.current = [p];
        ctx.strokeStyle = farbe;
        ctx.fillStyle = farbe;
        // Ein einzelner Tipp soll einen Punkt hinterlassen (i-Punkt).
        ctx.beginPath();
        ctx.arc(p.x, p.y, ctx.lineWidth / 2, 0, Math.PI * 2);
        ctx.fill();
    };

    const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!zeichnet.current) return;
        const ctx = canvasRef.current?.getContext("2d");
        if (!ctx) return;
        const pts = punkte.current;
        pts.push(punktVon(e));
        if (pts.length < 3) return;
        // Quadratische Kurve von Mitte zu Mitte, mit dem mittleren Punkt als Kontrollpunkt.
        const [a, b, c] = pts.slice(-3);
        const m1 = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const m2 = { x: (b.x + c.x) / 2, y: (b.y + c.y) / 2 };
        ctx.beginPath();
        ctx.moveTo(m1.x, m1.y);
        ctx.quadraticCurveTo(b.x, b.y, m2.x, m2.y);
        ctx.stroke();
    };

    const up = () => {
        if (!zeichnet.current) return;
        zeichnet.current = false;
        const ctx = canvasRef.current?.getContext("2d");
        const rest = schlussStueck(punkte.current);
        if (ctx && rest) {
            ctx.beginPath();
            ctx.moveTo(rest[0].x, rest[0].y);
            ctx.lineTo(rest[1].x, rest[1].y);
            ctx.stroke();
        }
        punkte.current = [];
        setLeer(false);
        void exportieren();
    };

    const leeren = () => {
        const c = canvasRef.current;
        const ctx = c?.getContext("2d");
        if (c && ctx) {
            ctx.save();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, c.width, c.height);
            ctx.restore();
        }
        setLeer(true);
        onAenderung(null);
    };

    return (
        <div className={s.wurzel}>
            <canvas
                ref={canvasRef}
                className={s.flaeche}
                aria-label="Zeichenfläche für die Unterschrift"
                onPointerDown={down}
                onPointerMove={move}
                onPointerUp={up}
                onPointerCancel={up}
                onPointerLeave={up}
            />
            <div className={s.fuss}>
                <span>{leer ? "Mit Maus, Stift oder Finger unterschreiben." : "Gefällt es nicht? Einfach neu beginnen."}</span>
                <button type="button" className={s.link} onClick={leeren} disabled={leer}>
                    Leeren
                </button>
            </div>
        </div>
    );
};
